import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash, Package, X } from '@phosphor-icons/react';
import HaleConsoleLayout from '../../components/hale/HaleConsoleLayout';
import { T, FONT, PageHeading, ConsoleCard, Eyebrow, StatusPill, EmptyState, Spinner } from './_theme';

const API = process.env.REACT_APP_BACKEND_URL;

const inputCls = 'w-full px-3.5 py-2.5 rounded-lg border text-[14px] outline-none transition-colors focus:border-[#E63946]';
const inputStyle = { borderColor: T.LINE, backgroundColor: T.WHITE, color: T.INK, fontFamily: FONT.BODY };
const blank = { product_name: '', category: '', inci_list: '', claimsText: '', documents: [] };

export default function HaleProductsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/hale-reviewed/products/mine`, { withCredentials: true });
      setItems(r.data);
    } catch (e) {
      if (e?.response?.status === 401) navigate('/hale/login', { replace: true });
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const addDoc = () => setF((p) => ({ ...p, documents: [...p.documents, { name: '', note: '' }] }));
  const setDoc = (i, k) => (e) => setF((p) => { const d = [...p.documents]; d[i] = { ...d[i], [k]: e.target.value }; return { ...p, documents: d }; });
  const rmDoc = (i) => setF((p) => ({ ...p, documents: p.documents.filter((_, j) => j !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!f.product_name.trim() || !f.category.trim()) { setError('Product name and category are required.'); return; }
    setBusy(true);
    try {
      const payload = {
        product_name: f.product_name.trim(),
        category: f.category.trim(),
        inci_list: f.inci_list.trim(),
        claims: f.claimsText.split('\n').map((c) => c.trim()).filter(Boolean),
        documents: f.documents.filter((d) => d.name.trim()).map((d) => ({ name: d.name.trim(), note: d.note?.trim() || null })),
      };
      await axios.post(`${API}/api/hale-reviewed/products`, payload, { withCredentials: true });
      setF(blank); setOpen(false);
      await load();
    } catch (e2) {
      setError(e2?.response?.data?.detail || 'Could not submit your product.');
    } finally { setBusy(false); }
  };

  return (
    <HaleConsoleLayout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-2">
        <PageHeading title="My Products" sub="Submit products for Hale Reviewed and track their status through approval." testid="hale-products-title" />
        <button onClick={() => setOpen(!open)} data-testid="hale-products-toggle"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-[13px] font-semibold mb-9" style={{ backgroundColor: open ? T.WHITE : T.RED, color: open ? T.INK : T.WHITE, border: open ? `1px solid ${T.LINE}` : 'none' }}>
          {open ? <><X size={15} weight="bold" /> Close</> : <><Plus size={15} weight="bold" /> Submit a product</>}
        </button>
      </div>

      {open && (
        <ConsoleCard className="mb-6" data-testid="hale-products-form">
          <form onSubmit={submit} className="space-y-4">
            {error && <div className="px-4 py-2.5 rounded-lg text-[13px]" style={{ backgroundColor: 'rgba(185,28,28,0.06)', color: '#B91C1C' }} data-testid="hale-products-error">{error}</div>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className="block text-[12px] font-semibold mb-1.5">Product Name</label><input value={f.product_name} onChange={set('product_name')} className={inputCls} style={inputStyle} data-testid="hale-products-name" /></div>
              <div><label className="block text-[12px] font-semibold mb-1.5">Category</label><input value={f.category} onChange={set('category')} className={inputCls} style={inputStyle} placeholder="e.g. Beard care, Skincare" data-testid="hale-products-category" /></div>
            </div>
            <div><label className="block text-[12px] font-semibold mb-1.5">INCI List</label><textarea rows={3} value={f.inci_list} onChange={set('inci_list')} className={inputCls} style={inputStyle} placeholder="Aqua, Glycerin, …" data-testid="hale-products-inci" /></div>
            <div><label className="block text-[12px] font-semibold mb-1.5">Claims <span className="font-normal" style={{ color: T.MUTED }}>(one per line)</span></label><textarea rows={3} value={f.claimsText} onChange={set('claimsText')} className={inputCls} style={inputStyle} placeholder={'Hydrating\nDermatologist tested'} data-testid="hale-products-claims" /></div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[12px] font-semibold">Supporting Documents <span className="font-normal" style={{ color: T.MUTED }}>(reference only)</span></label>
                <button type="button" onClick={addDoc} className="text-[12px] font-semibold inline-flex items-center gap-1" style={{ color: T.RED }} data-testid="hale-products-adddoc"><Plus size={13} weight="bold" /> Add</button>
              </div>
              {f.documents.map((d, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <input value={d.name} onChange={setDoc(i, 'name')} placeholder="Document name (e.g. COA.pdf)" className={inputCls} style={inputStyle} />
                  <input value={d.note} onChange={setDoc(i, 'note')} placeholder="Note (optional)" className={inputCls} style={inputStyle} />
                  <button type="button" onClick={() => rmDoc(i)} className="px-2.5 rounded-lg border" style={{ borderColor: T.LINE, color: T.MUTED }}><Trash size={14} /></button>
                </div>
              ))}
            </div>
            <button type="submit" disabled={busy} className="px-6 py-3 rounded-lg text-[13px] font-semibold disabled:opacity-60" style={{ backgroundColor: T.RED, color: T.WHITE }} data-testid="hale-products-submit">
              {busy ? 'Submitting…' : 'Submit for Hale Reviewed'}
            </button>
          </form>
        </ConsoleCard>
      )}

      {loading && <Spinner label="Loading your products…" />}

      {!loading && items.length === 0 && !open && (
        <EmptyState icon={Package} title="No products submitted yet" desc="Use “Submit a product” above to begin the Hale Reviewed process." testid="hale-products-empty" />
      )}

      {!loading && items.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="hale-products-list">
          {items.map((p) => (
            <ConsoleCard key={p.id} data-testid={`hale-product-card-${p.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <p className="text-[16px] font-bold" style={{ fontFamily: FONT.DISPLAY, color: T.INK }}>{p.product_name}</p>
                <StatusPill status={p.status} />
              </div>
              <p className="text-[12px] mb-3" style={{ color: T.MUTED }}>{p.category}</p>
              <div className="flex gap-4 text-[12px]" style={{ color: T.MUTED, fontFamily: FONT.MONO }}>
                <span>{p.claims?.length || 0} claims</span>
                <span>{p.documents?.length || 0} docs</span>
                <span>{p.submitted_at ? new Date(p.submitted_at).toLocaleDateString() : ''}</span>
              </div>
            </ConsoleCard>
          ))}
        </div>
      )}
    </HaleConsoleLayout>
  );
}
